import { useAtomValue } from "jotai";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useExpenseQuery } from "web_myplann/Api/Finances/useExpenseQuery";
import { useIncomeQuery } from "web_myplann/Api/Finances/useIncomeQuery";
import { usePatchFinanceMutation } from "web_myplann/Api/Finances/usePatchFinanceMutation";
import { financeState } from "web_myplann/Finances/State/FinanceAtom";
import type { FinancesNavigationState } from "web_myplann/Finances/Types/FinancesNavigationState";
import type { FinancialPlanUpdateRequestDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	ApiCallError,
	appSessionStartedAt,
} from "web_myplann/Infrastructure/Api/ApiUtils";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { useLocationState } from "web_myplann/Shared/Hooks/useLocationState";

const hasSessionFreshData = (dataUpdatedAt: number) =>
	dataUpdatedAt >= appSessionStartedAt;

type UseFinishFinancesParams = {
	runUnguarded?: (action: () => void) => void;
};

export const useFinishFinances = ({
	runUnguarded = (action) => action(),
}: UseFinishFinancesParams = {}) => {
	const { t } = useResource();
	const finance = useAtomValue(financeState);
	const navigate = useNavigate();
	const navigationState = useLocationState<FinancesNavigationState>();
	const { mutateAsync, isPending } = usePatchFinanceMutation();
	const { dataUpdatedAt: incomeUpdatedAt } = useIncomeQuery();
	const { dataUpdatedAt: expensesUpdatedAt } = useExpenseQuery();

	const showSaveError = () => {
		toast.error(t(Resources.Finances.Offline.saveError));
	};

	const finishFinances = async () => {
		const hasFreshIncome = hasSessionFreshData(incomeUpdatedAt);
		const hasFreshExpenses = hasSessionFreshData(expensesUpdatedAt);

		if (!hasFreshIncome || !hasFreshExpenses) {
			showSaveError();
			return;
		}

		const requestContract: FinancialPlanUpdateRequestDTO = {
			incomes: finance.income,
			expenses: finance.expenses,
		};

		try {
			await mutateAsync(requestContract);
			runUnguarded(() =>
				navigate(Routing.Finances.Intro.path(), {
					replace: true,
					state: {
						from: navigationState.from,
					} satisfies FinancesNavigationState,
				}),
			);
		} catch (error) {
			if (error instanceof ApiCallError && error.data.status === 0) {
				showSaveError();
			}
		}
	};

	return { finishFinances, isPending };
};
